import * as React from 'react'

import { clsx } from 'clsx'

import { Flex } from '@buffer-mono/popcorn'

import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import {
  EngagementMediaAsset,
  EngagementMediaAsset_Asset,
} from './EngagementMediaAsset'

import styles from './EngagementMedia.module.css'

import type { JSX } from 'react'

type EngagementMediaProps = {
  /** Media assets attached to the engagement. */
  assets: FragmentOf<typeof EngagementMediaAsset_Asset>[]
  className?: string
  /**
   * Rendered in place of the strip when no asset can be reached. Pass one
   * where hiding the media leaves the engagement with nothing to show.
   */
  fallback?: React.ReactNode
}

/** Stable empty default, so the initial state never busts a memo downstream. */
const NONE_UNREACHABLE: ReadonlySet<string> = new Set()

/**
 * Presentational media strip for an engagement row (comment or mention):
 * a horizontally scrollable row of attached-media thumbnails. Prop-driven —
 * the context-bound `CommentMedia` / `MentionMedia` feed it their assets.
 *
 * A thumbnail the browser can't fetch is dropped rather than drawn as a
 * placeholder: a broken frame reads as a Buffer bug, an absent one reads as
 * content we don't have. Unreachability is tracked by thumbnail url, so a
 * re-hosted asset gets a fresh attempt.
 */
export const EngagementMedia = ({
  assets,
  className,
  fallback = null,
}: EngagementMediaProps): JSX.Element | null => {
  const [unreachableThumbnails, setUnreachableThumbnails] =
    React.useState<ReadonlySet<string>>(NONE_UNREACHABLE)

  const markUnreachable = React.useCallback((thumbnail: string): void => {
    setUnreachableThumbnails((current) => new Set(current).add(thumbnail))
  }, [])

  if (!assets?.length) return null

  const reachableAssets = assets
    .map((asset) => ({
      asset,
      thumbnail: readFragment(EngagementMediaAsset_Asset, asset).thumbnail,
    }))
    .filter(({ thumbnail }) => !unreachableThumbnails.has(thumbnail))

  if (!reachableAssets.length) return <>{fallback}</>

  return (
    <Flex
      className={clsx(styles.assets, className)}
      gap="2xs"
      data-testid="engagement-media"
    >
      {reachableAssets.map(({ asset, thumbnail }) => (
        <EngagementMediaAsset
          key={thumbnail}
          asset={asset}
          onUnreachable={() => markUnreachable(thumbnail)}
        />
      ))}
    </Flex>
  )
}

EngagementMedia.displayName = 'EngagementMedia'
